import moment from 'moment'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import downloadAsCsv from 'utils/downloadAsCsv'

import { hotelStatusNamesFilterable } from 'main/hotelStatuses'
import { parentChainNames } from 'main/hotelBrandsAndChains'

import { getHealthCheckBillingInvoiceData, getRichHotelData } from '../../../actions/healthCheck'
import CustomHeader from '../../../components/ReusableComponents/HeaderTitles'
import LoadingIndicator from '../../../components/ReusableComponents/LoadingIndicator'
import Pagination from '../../../components/ReusableComponents/Pagination'
import Table from '../../../components/ReusableComponents/Table'
import Tab from '../../../components/ReusableComponents/Tabs'
import dashboardApi from '../../../services/api/dashboard'
import useHealthCheckFilters from '../Common/useHealthCheckFilters'
import BillingFilters from './BillingFilters'
import styles from './Billing.module.scss'
import {
  healthCheckBillingInvoiceStatusFilterOptions,
  healthCheckBillingInvoiceStatusLabels
} from './invoiceStatusNames'
import BillingRow from './Row'
import BillingTitleRow from './TitleRow'
import TotalRow from './TotalRow'
import GroupedRow from './GroupedRow'

const DEFAULT_PAGE_SIZE = 20

const SORT_FIELD_BY_INDEX = [
  null,
  null,
  'displayId',
  'shortName',
  'csmUserFullName',
  'subsidiaryInvoiceId',
  'balanceState',
  'invoiceDate',
  'dueDate',
  'agingDays',
  'defaultPaymentMethod',
  'totalAmountDue',
  'balanceRemaining',
  'promiseToPayDate',
  null
]

function formatPrice(number) {
  return parseFloat(number).toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD'
  })
}

const HealthCheckBilling = () => {
  const dispatch = useDispatch()

  const [filterState, filterDispatch] = useHealthCheckFilters('billing')

  const [sortByIndex, setSortByIndex] = useState(12)
  const [sortDecreasing, setSortDecreasing] = useState(true)
  const [pageIndex, setPageIndex] = useState(1)
  const [selectedPageSize, setSelectedPageSize] = useState(() => {
    const saved = localStorage.getItem('healthCheck-billing-pageSize')
    return ['20', '50', '100', '200', 'all'].includes(saved)
      ? saved
      : `${DEFAULT_PAGE_SIZE}`
  })
  const filterOptionsRequestedRef = useRef(false)
  const topScrollRef = useRef(null)
  const tableScrollRef = useRef(null)
  const isSyncingScrollRef = useRef(false)

  const [groupByHotelGroup, setGroupByHotelGroup] = useState(() => {
    const saved = localStorage.getItem('healthCheck-billing-groupByHotelGroup')
    return saved === 'true'
  })
  const [expandedGroups, setExpandedGroups] = useState(() => {
    const saved = localStorage.getItem('healthCheck-billing-expandedGroups')
    return saved ? JSON.parse(saved) : {}
  })

  const isLoading = useSelector(s => s.healthCheck.billings.isLoading)
  const hotelData = useSelector(s => s.healthCheck.billings.hotelData)
  const allhotelGroups = useSelector(s => s.healthCheck.billings.hotelGroups)
  const allhotelChains = useSelector(s => s.healthCheck.billings.hotelChains)
  const totalCount = useSelector(s => s.healthCheck.billings.totalCount)
  const totalBalanceRemaining = useSelector(s => s.healthCheck.billings.totalBalanceRemaining)
  const pageBalanceRemaining = useSelector(s => s.healthCheck.billings.pageBalanceRemaining)
  const pageItemCount = useSelector(s => s.healthCheck.billings.pageItemCount)
  const pageSize = useSelector(s => s.healthCheck.billings.pageSize)

  const syncHorizontalScroll = useCallback((source, target) => {
    if (!source || !target || isSyncingScrollRef.current) {
      return
    }

    isSyncingScrollRef.current = true
    target.scrollLeft = source.scrollLeft
    requestAnimationFrame(() => {
      isSyncingScrollRef.current = false
    })
  }, [])

  useEffect(() => {
    const topEl = topScrollRef.current
    const tableEl = tableScrollRef.current
    if (!topEl || !tableEl) {
      return undefined
    }

    const onTopScroll = () => syncHorizontalScroll(topEl, tableEl)
    const onTableScroll = () => syncHorizontalScroll(tableEl, topEl)

    topEl.addEventListener('scroll', onTopScroll)
    tableEl.addEventListener('scroll', onTableScroll)

    return () => {
      topEl.removeEventListener('scroll', onTopScroll)
      tableEl.removeEventListener('scroll', onTableScroll)
    }
  }, [syncHorizontalScroll, isLoading, hotelData])

  const { showAlert, showWarning, showGreen } = filterState

  const buildRequestParams = useCallback(
    (overrides = {}) => {
      const sortField = SORT_FIELD_BY_INDEX[sortByIndex]
      const includeFilterOptions =
        overrides.includeFilterOptions ??
        (!filterOptionsRequestedRef.current && pageIndex === 1)

      if (includeFilterOptions) {
        filterOptionsRequestedRef.current = true
      }

      return {
        pageIndex,
        pageSize:
          selectedPageSize === 'all'
            ? DEFAULT_PAGE_SIZE
            : Number.parseInt(selectedPageSize, 10),
        exportAll: selectedPageSize === 'all',
        sortBy: sortField || 'balanceRemaining',
        sortDescending: sortDecreasing,
        searchString: filterState.searchString || '',
        hotelGroups: filterState.hotelGroups || 'all',
        chains: filterState.chains || 'all',
        brands: filterState.brands || 'all',
        subsidiaryStatus: filterState.subsidiaryStatus || [],
        invoiceStatuses: filterState.invoiceStatuses || [],
        csmIds: filterState.csmIds || [],
        showAlert: filterState.showAlert,
        showWarning: filterState.showWarning,
        showGreen: filterState.showGreen,
        agingGreaterThanZero: filterState.agingGreaterThanZero,
        includeFilterOptions,
        ...overrides
      }
    },
    [filterState, pageIndex, selectedPageSize, sortByIndex, sortDecreasing]
  )

  const filterAndSortKey = useMemo(
    () =>
      JSON.stringify({
        filterState,
        selectedPageSize,
        sortByIndex,
        sortDecreasing
      }),
    [filterState, selectedPageSize, sortByIndex, sortDecreasing]
  )
  const prevFilterAndSortKeyRef = useRef(filterAndSortKey)
  const pendingPageResetRef = useRef(false)

  useEffect(() => {
    if (prevFilterAndSortKeyRef.current !== filterAndSortKey) {
      prevFilterAndSortKeyRef.current = filterAndSortKey
      pendingPageResetRef.current = true
      setPageIndex(1)
    }
  }, [filterAndSortKey])

  useEffect(() => {
    if (pendingPageResetRef.current && pageIndex !== 1) {
      return
    }

    pendingPageResetRef.current = false
    dispatch(getHealthCheckBillingInvoiceData(buildRequestParams()))
  }, [dispatch, buildRequestParams, pageIndex, filterAndSortKey])

  const totalPages = useMemo(
    () =>
      selectedPageSize === 'all'
        ? 1
        : Math.max(1, Math.ceil(totalCount / (pageSize || DEFAULT_PAGE_SIZE))),
    [totalCount, pageSize, selectedPageSize]
  )

  const results = hotelData
  const pageOffset =
    selectedPageSize === 'all'
      ? 0
      : (pageIndex - 1) * Number.parseInt(selectedPageSize, 10)

  const calculateRowStatus = row => {
    const { agingDays, balanceState, serviceBlockOn } = row

    if (serviceBlockOn) {
      return 'alert'
    }

    if (
      balanceState === 'PaymentOverdue' ||
      balanceState === 'PaymentFailed' ||
      agingDays > 30
    ) {
      return 'alert'
    }

    if (agingDays > 0 || balanceState === 'PaidPartially' || balanceState === 'PaymentPending') {
      return 'warning'
    }

    if (agingDays <= 0) {
      return 'green'
    }

    return 'warning'
  }

  const getStatusExplanation = row => {
    const { agingDays, balanceState, serviceBlockOn, balanceRemaining } = row
    const status = calculateRowStatus(row)
    const reasons = []

    if (status === 'alert') {
      if (serviceBlockOn) {
        const daysUntilBlock = moment(serviceBlockOn).diff(moment(), 'days')
        reasons.push(
          `Service will be blocked on ${moment(serviceBlockOn).format('MMM DD, YYYY')} (${daysUntilBlock} days)`
        )
      }
      if (balanceState === 'PaymentOverdue' || balanceState === 'PaymentFailed') {
        reasons.push(
          `Balance state: ${healthCheckBillingInvoiceStatusLabels[balanceState] || balanceState}`
        )
      }
      if (agingDays > 30) {
        reasons.push(`Overdue by ${agingDays} days`)
      }
    } else if (status === 'warning') {
      if (agingDays > 0) {
        reasons.push(`Aging: ${agingDays} days`)
      }
      reasons.push(`Open balance: ${formatPrice(balanceRemaining)}`)
    } else if (status === 'green') {
      reasons.push(`Not yet due (${agingDays} days until due date)`)
      reasons.push(`Open balance: ${formatPrice(balanceRemaining)}`)
    }

    return reasons.length > 0 ? reasons.join('  ') : 'Status: ' + status
  }

  function handleTitleCellClick(rowIndex) {
    if (sortByIndex === rowIndex) {
      setSortDecreasing(d => !d)
    }

    setSortByIndex(rowIndex)
  }

  async function handleClickDownloadAsCsv() {
    try {
      const response = await dashboardApi.getHealthCheckBillingInvoices(
        buildRequestParams({
          exportAll: true,
          includeFilterOptions: false,
          pageIndex: 1,
          pageSize: DEFAULT_PAGE_SIZE
        })
      )

      const exportItems = getRichHotelData(response?.items ?? response?.Items ?? [])

      const {
        searchString,
        chains,
        brands,
        hotelGroups,
        subsidiaryStatus,
        invoiceStatuses,
        csmIds,
        agingGreaterThanZero
      } = filterState

      const getFilterLabel = (value, allOptions, defaultLabel) => {
        if (value === 'all' || !value || (Array.isArray(value) && value.length === 0)) {
          return 'All'
        }
        if (Array.isArray(value)) {
          return value.map(v => allOptions[v] || v).join(', ')
        }
        return allOptions[value] || value || defaultLabel
      }

      const getCsmLabel = () => {
        if (!csmIds || csmIds.length === 0) return 'All'
        return csmIds.join(', ')
      }

      const getStatusLabel = () => {
        if (!subsidiaryStatus || subsidiaryStatus.length === 0) return 'All'
        return subsidiaryStatus.map(s => hotelStatusNamesFilterable[s] || s).join(', ')
      }

      const getInvoiceStatusLabel = () => {
        if (!invoiceStatuses || invoiceStatuses.length === 0) return 'All'
        return invoiceStatuses
          .map(s => healthCheckBillingInvoiceStatusFilterOptions[s] || s)
          .join(', ')
      }

      const filterMetadata = [
        ['Filter Values Applied:'],
        ['Hotel Group', getFilterLabel(hotelGroups, allhotelGroups, 'All')],
        ['Brand', getFilterLabel(brands, parentChainNames, 'All')],
        ['Chain', getFilterLabel(chains, allhotelChains, 'All')],
        ['Status', getStatusLabel()],
        ['Invoice Status', getInvoiceStatusLabel()],
        ['CSM', getCsmLabel()],
        ['Search', searchString || 'None'],
        [
          'Status Filters',
          `Alert: ${showAlert ? 'Yes' : 'No'}, Warning: ${showWarning ? 'Yes' : 'No'}, Green: ${showGreen ? 'Yes' : 'No'}`
        ],
        ['Aging > 0 only', agingGreaterThanZero ? 'Yes' : 'No'],
        []
      ]

      const titleRow = [
        'Hotel ID',
        'Hotel Name',
        'Hotel Group',
        'Brand',
        'Chain',
        'CSM',
        'Invoice ID',
        'Invoice Period Start',
        'Invoice Period End',
        'Balance State',
        'Invoice Date',
        'Due Date',
        'Aging (days)',
        'Default Payment Method',
        'Invoice Amount',
        'Overdue Amount',
        'PTP Date',
        'Service Block Date'
      ]

      const resAsArrays = exportItems.map(b => {
        const brand = b.brand || ''
        const shortName = b.shortName || b.hotelName || ''
        return [
          b.displayId,
          `${brand}-${shortName}`,
          b.groupName || '-',
          b.parentChainCode || '-',
          b.chainCode || '-',
          b.csmUserFullName || '-',
          b.subsidiaryInvoiceId,
          b.invoiceBeginDate ? moment(b.invoiceBeginDate).format('MM/DD/YYYY') : '-',
          b.invoiceEndDate ? moment(b.invoiceEndDate).format('MM/DD/YYYY') : '-',
          healthCheckBillingInvoiceStatusLabels[b.balanceState] || b.balanceState || '-',
          b.invoiceDate ? moment(b.invoiceDate).format('MM/DD/YYYY') : '-',
          b.dueDate ? moment(b.dueDate).format('MM/DD/YYYY') : '-',
          b.agingDays,
          b.defaultPaymentMethod || '-',
          formatPrice(b.totalAmountDue),
          formatPrice(b.balanceRemaining),
          b.promiseToPayDate ? moment(b.promiseToPayDate).format('MM/DD/YYYY') : '-',
          b.serviceBlockOn ? moment(b.serviceBlockOn).format('MM/DD/YYYY') : '-'
        ]
      })

      const dataArray = [...filterMetadata, titleRow, ...resAsArrays]
      const fileName = `health_check_billing_invoices_${moment().format('YYYY-MM-DD')}.csv`

      downloadAsCsv(dataArray, fileName)
    } catch (err) {
      // export failed silently; user can retry
    }
  }

  return (
    <div className={styles.wrapper}>
      <CustomHeader title="Hotel health check" width="100%" isFixed />
      <Tab.Parent>
        <Tab to="/healthCheck/pms">PMS</Tab>
        <Tab to="/healthCheck/autoinvite">AutoInvite</Tab>
        <Tab to="/healthCheck/billing">Billing</Tab>
        <Tab to="/healthCheck/performance">Performance</Tab>
        <Tab to="/healthCheck/summary">Summary</Tab>
      </Tab.Parent>

      <div className={styles.pageBody}>
        <BillingFilters
          state={filterState}
          dispatch={filterDispatch}
          allhotelGroups={allhotelGroups}
          allhotelChains={allhotelChains}
          groupByHotelGroup={groupByHotelGroup}
          onGroupByHotelGroupChange={newValue => {
            setGroupByHotelGroup(newValue)
            localStorage.setItem('healthCheck-billing-groupByHotelGroup', String(newValue))
          }}
          pageSize={selectedPageSize}
          onPageSizeChange={newValue => {
            setSelectedPageSize(newValue)
            localStorage.setItem('healthCheck-billing-pageSize', newValue)
          }}
          onDownloadCsv={handleClickDownloadAsCsv}
        />

        {!isLoading && (
          <TotalRow
            pageBalanceRemaining={pageBalanceRemaining}
            pageItemCount={pageItemCount}
            totalBalanceRemaining={totalBalanceRemaining}
            totalCount={totalCount}
            showPageTotals={totalPages > 1}
          />
        )}

        <div className={styles.tableScrollTop} ref={topScrollRef} aria-hidden="true">
          <div className={styles.tableScrollTopSpacer} />
        </div>
        <div
          className={`${styles.tableContainer} ${styles.billingTableGrid}`}
          ref={tableScrollRef}
        >
          {isLoading ? (
            <div className={styles.tableLoading}>
              <LoadingIndicator marginTop={0} />
            </div>
          ) : (
            <Table
              handleTitleCellClick={handleTitleCellClick}
              sortIndex={sortByIndex}
              sortDirection={sortDecreasing ? 'down' : 'up'}
            >
              <BillingTitleRow />
              {groupByHotelGroup
                ? (() => {
                    const grouped = results.reduce((acc, hotel) => {
                      const groupName = hotel.groupName || 'Ungrouped'
                      if (!acc[groupName]) {
                        acc[groupName] = []
                      }
                      acc[groupName].push(hotel)
                      return acc
                    }, {})

                    const sortedGroups = Object.keys(grouped).sort()
                    let globalIndex = pageOffset + 1

                    return sortedGroups.map(groupName => {
                      const groupHotels = grouped[groupName]
                      const groupKey = groupName || 'ungrouped'
                      const isExpanded = expandedGroups[groupKey] !== false

                      return (
                        <GroupedRow
                          key={groupKey}
                          groupName={groupName}
                          count={groupHotels.length}
                          isExpanded={isExpanded}
                          onToggle={() => {
                            setExpandedGroups(prev => {
                              const newState = {
                                ...prev,
                                [groupKey]: !isExpanded
                              }
                              localStorage.setItem(
                                'healthCheck-billing-expandedGroups',
                                JSON.stringify(newState)
                              )
                              return newState
                            })
                          }}
                        >
                          {groupHotels.map(h => {
                            const rowStatus = calculateRowStatus(h)
                            const statusExplanation = getStatusExplanation(h)
                            const currentIndex = globalIndex++
                            return (
                              <BillingRow
                                key={`${h.subsidiaryId}-${h.subsidiaryInvoiceId}`}
                                {...h}
                                index={currentIndex}
                                rowStatus={rowStatus}
                                statusExplanation={statusExplanation}
                              />
                            )
                          })}
                        </GroupedRow>
                      )
                    })
                  })()
                : results.map((h, i) => {
                    const rowStatus = calculateRowStatus(h)
                    const statusExplanation = getStatusExplanation(h)
                    return (
                      <BillingRow
                        key={`${h.subsidiaryId}-${h.subsidiaryInvoiceId}`}
                        {...h}
                        index={pageOffset + i + 1}
                        rowStatus={rowStatus}
                        statusExplanation={statusExplanation}
                      />
                    )
                  })}
            </Table>
          )}
        </div>

        {!isLoading && totalPages > 1 && (
          <div className={styles.paginationWrapper}>
            <Pagination
              totalPages={totalPages}
              activePageIndex={pageIndex}
              onPageChange={setPageIndex}
            />
          </div>
        )}
      </div>
    </div>
  )
}

export default HealthCheckBilling
